/** Display order: Pulse (main) → Snap → Ladder. Internal ids unchanged. */
export const STRATEGY_CATALOG = {
    volume_churn: {
        id: 'volume_churn',
        name: 'AI Chip · Pulse',
        short: 'Momentum chip — reads the range and fires dense long/short setups',
        description: 'AI Chip · Pulse watches the box in real time: breakout / pullback on each side. The chart sketch updates instantly; Start runs the same chip live. Short dial stays stricter.',
        howItWorks: [
            'Draw a range — Pulse only trades inside that box while it exists.',
            'Enable Long / Short; each side has a more↔fewer dial.',
            'Sketch markers (sk L / S / ×) appear immediately as you tune dials.',
            'Averaging uses adverse step until max clips; soft exits gate on only-profit.',
            'Fills arm on the closed bar and execute at the next open.',
        ],
        simpleDesk: true,
        defaults: {
            longEnabled: true,
            shortEnabled: true,
            longTradeDensity: 55,
            shortTradeDensity: 24,
            // Legacy single-bias kept for old runs; new desk ignores when toggles present
            sideBias: 'long',
            entryMode: 'both',
            pullbackPct: 0.45,
            breakoutLookback: 10,
            holdBars: 12,
            maxOrders: 3,
            stepPercent: 0.55,
            onlyProfit: { enabled: true, minProfitPct: 0.15 },
        },
        fields: [
            { key: 'longEnabled', label: 'Long', type: 'boolean' },
            { key: 'shortEnabled', label: 'Short', type: 'boolean' },
            { key: 'longTradeDensity', label: 'Long trades', type: 'density', min: 0, max: 100, step: 1, hint: 'fewer ↔ more' },
            { key: 'shortTradeDensity', label: 'Short trades', type: 'density', min: 0, max: 100, step: 1, hint: 'fewer ↔ more (stricter)' },
            { key: 'maxOrders', label: 'Max clips', type: 'number', min: 1, max: 12, step: 1, hint: 'entry + averaging' },
        ],
    },
    hideep: {
        id: 'hideep',
        name: 'AI Chip · Snap',
        short: 'Mean-reversion chip — snaps entries at SMA/RSI extremes (+ fractal)',
        description: 'AI Chip · Snap waits for deep pullbacks vs SMA + RSI. Dial “more” relaxes depth; Fractal wings (0=off … 5=strict) confirm pivots. Fewer trades than Pulse — at extremes.',
        howItWorks: [
            'Runs only inside the strategy price range when one is set.',
            'Long / short “more↔fewer” dials set RSI depth + SMA distance.',
            'Fractal wings: 0 = no fractal, 1–5 = Williams confirm (higher = fewer entries).',
            'Soft exit when RSI unwinds; averaging uses max clips + adverse step.',
            'Dials alone do nothing until you press ▶ Start strategy (tab shows live).',
        ],
        simpleDesk: true,
        defaults: {
            smaLength: 10,
            rsiLength: 5,
            longEnabled: true,
            shortEnabled: true,
            longTradeDensity: 50,
            shortTradeDensity: 22,
            fractalWings: 1,
            maxOrders: 3,
            stepPercent: 0.55,
            onlyProfit: { enabled: true, minProfitPct: 0.15 },
        },
        fields: [
            { key: 'longEnabled', label: 'Long', type: 'boolean' },
            { key: 'shortEnabled', label: 'Short', type: 'boolean' },
            { key: 'longTradeDensity', label: 'Long trades', type: 'density', min: 0, max: 100, step: 1, hint: 'fewer ↔ more' },
            { key: 'shortTradeDensity', label: 'Short trades', type: 'density', min: 0, max: 100, step: 1, hint: 'fewer ↔ more (stricter)' },
            { key: 'fractalWings', label: 'Fractal wings', type: 'number', min: 0, max: 5, step: 1, hint: '0=off · 1–5 Williams' },
            { key: 'maxOrders', label: 'Max clips', type: 'number', min: 1, max: 12, step: 1, hint: 'entry + averaging' },
        ],
    },
    redflag_dca: {
        id: 'redflag_dca',
        name: 'AI Chip · Ladder',
        short: 'Grid chip — laddered averaging with TP on each leg',
        description: 'AI Chip · Ladder builds a DCA ladder from the arm price. Long steps down, short steps up; each leg has its own TP. Same desk dials as Pulse / Snap.',
        howItWorks: [
            'Anchor = price when you arm (or first fill).',
            'Long: steps down; Short: steps up. Each leg has its own TP.',
            '“More trades” → tighter step (denser ladder); shorts start wider.',
            'Max clips caps ladder depth; only-profit dial gates soft exits.',
        ],
        simpleDesk: true,
        defaults: {
            longEnabled: true,
            shortEnabled: false,
            longTradeDensity: 55,
            shortTradeDensity: 28,
            stepPercent: 0.15,
            tpPercent: 0.15,
            maxOrders: 6,
            onlyProfit: { enabled: true, minProfitPct: 0.15 },
        },
        fields: [
            { key: 'longEnabled', label: 'Long', type: 'boolean' },
            { key: 'shortEnabled', label: 'Short', type: 'boolean' },
            { key: 'longTradeDensity', label: 'Long trades', type: 'density', min: 0, max: 100, step: 1, hint: 'fewer ↔ more (tighter grid)' },
            { key: 'shortTradeDensity', label: 'Short trades', type: 'density', min: 0, max: 100, step: 1, hint: 'fewer ↔ more (stricter)' },
            { key: 'maxOrders', label: 'Max clips', type: 'number', min: 1, max: 30, step: 1, hint: 'grid depth' },
        ],
    },
    order_book: {
        id: 'order_book',
        name: 'AI Chip · Book',
        short: 'Order-block chip — demand/supply bricks on the chart',
        description: 'AI Chip · Book trades the same OB boxes you see on the chart. Enter on tap/approach (nearer brick wins over a far wick); stop = far-edge bust; take = holdBars / opposing brick. Density = how picky + how near.',
        howItWorks: [
            'Turn on OB overlay (auto when you pick Book) — those boxes are the book.',
            'Long / Short + more↔fewer: strength floor, approach distance, fresh vs tested.',
            'Entry when a closed bar taps / approaches a qualifying OB (nearer brick wins over a far wick); sketch = live. Stop = far edge bust; take = holdBars / opposing brick.',
            'Hard invalidate beyond far edge; soft exit on opposing OB or holdBars + only-profit.',
            'Panel line: wait / enter + brick prices + strength so it is not a black box.',
        ],
        simpleDesk: true,
        defaults: {
            longEnabled: true,
            shortEnabled: true,
            longTradeDensity: 55,
            shortTradeDensity: 40,
            holdBars: 10,
            maxOrders: 2,
            stepPercent: 0.4,
            onlyProfit: { enabled: true, minProfitPct: 0.15 },
        },
        fields: [
            { key: 'longEnabled', label: 'Long', type: 'boolean' },
            { key: 'shortEnabled', label: 'Short', type: 'boolean' },
            { key: 'longTradeDensity', label: 'Long trades', type: 'density', min: 0, max: 100, step: 1, hint: 'fewer ↔ more / nearer bricks' },
            { key: 'shortTradeDensity', label: 'Short trades', type: 'density', min: 0, max: 100, step: 1, hint: 'fewer ↔ more (stricter)' },
            { key: 'maxOrders', label: 'Max clips', type: 'number', min: 1, max: 8, step: 1, hint: 'entry + adds in same brick' },
        ],
    },
};
export const strategyList = () => [
    STRATEGY_CATALOG.volume_churn,
    STRATEGY_CATALOG.hideep,
    STRATEGY_CATALOG.redflag_dca,
    STRATEGY_CATALOG.order_book,
];
